import { forwardRef, type HTMLAttributes } from 'react';
import styles from './Skeleton.module.css';

export type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'card';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual variant of the skeleton */
  variant?: SkeletonVariant;
  /** Width of the skeleton (CSS value) */
  width?: string | number;
  /** Height of the skeleton (CSS value) */
  height?: string | number;
  /** Number of text lines to render (only for text variant) */
  lines?: number;
  /** Whether to animate the skeleton */
  animation?: boolean;
}

/**
 * Skeleton component for loading states.
 * Provides visual placeholders while content is loading.
 *
 * @example
 * ```tsx
 * // Text skeleton
 * <Skeleton variant="text" lines={3} />
 *
 * // Card skeleton
 * <Skeleton variant="card" height={200} />
 *
 * // Custom dimensions
 * <Skeleton width={100} height={100} variant="circular" />
 * ```
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'text',
      width,
      height,
      lines = 1,
      animation = true,
      className,
      style,
      ...props
    },
    ref
  ) => {
    const baseClassNames = [
      styles.skeleton,
      styles[variant],
      animation && styles.animated,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const customStyle = {
      ...style,
      ...(width !== undefined && { width: typeof width === 'number' ? `${width}px` : width }),
      ...(height !== undefined && { height: typeof height === 'number' ? `${height}px` : height }),
    };

    // For text variant with multiple lines
    if (variant === 'text' && lines > 1) {
      return (
        <div
          ref={ref}
          className={styles.textContainer}
          role="status"
          aria-label="Loading"
          {...props}
        >
          {Array.from({ length: lines }).map((_, index) => (
            <div
              key={index}
              className={[
                styles.skeleton,
                styles.text,
                animation && styles.animated,
                index === lines - 1 && styles.lastLine,
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                animationDelay: `${index * 100}ms`,
              }}
            />
          ))}
        </div>
      );
    }

    // For card variant
    if (variant === 'card') {
      return (
        <div
          ref={ref}
          className={[styles.cardContainer, animation && styles.animated, className]
            .filter(Boolean)
            .join(' ')}
          style={customStyle}
          role="status"
          aria-label="Loading"
          {...props}
        >
          <div className={styles.cardImage} />
          <div className={styles.cardContent}>
            <div className={[styles.skeleton, styles.text, styles.cardTitle].filter(Boolean).join(' ')} />
            <div className={[styles.skeleton, styles.text, styles.cardSubtitle].filter(Boolean).join(' ')} />
            <div className={styles.cardMeta}>
              <div className={[styles.skeleton, styles.rectangular, styles.cardChip].filter(Boolean).join(' ')} />
              <div className={[styles.skeleton, styles.rectangular, styles.cardChip].filter(Boolean).join(' ')} />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={baseClassNames}
        style={customStyle}
        role="status"
        aria-label="Loading"
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';
